<template>
  <div class="display-in-center view relative" v-loading.fullscreen="loading">
    <div v-if="barcode">
      <div class="current-tracking-point-container">{{ currentTrackPointName }}</div>
      <BarcodeLabel
        v-if="!isQuickTrackingMode"
        :label="labelOfBC(barcode)"
        :subLabel="barcode.id"
        :type="barcode.barcodeType"
      />
      <div class="step-label">{{ $t('Create Tracking Data') }}</div>
      <div>
        <div class="section section-trackpoints">
          <div class="section-header">{{ $t('Next tracking point is') }}</div>
          <hr class="line-break" />

          <el-radio
            v-model="selectedTarget"
            v-for="trackpoint in targetChoices"
            class="next-trackingPoint"
            :key="trackpoint.key"
            :label="trackpoint.key"
            :disabled="isDisabledEditing(trackpoint.key)"
          >
            {{ trackpoint.name }}
          </el-radio>
        </div>
      </div>

      <div class="section-header shrink">
        {{ selectedStep }}
      </div>
      <hr class="line-break" />
      <FormBuilder
        :isShownFloatingActionButton="isFABButtonAvailable"
        :passedButtons="allowSkipTracking ? buttonsArrayInTrack : []"
        v-if="selectedTarget"
        :key="formBuilderKey"
        :formSchema="selectedFormSchema"
        :formModel="formModel"
        @submit="confirmSubmitDialog"
        @reviewCalculation="reviewCalculation"
      >
        <template v-slot:bottom-form>
          <div v-if="hasRemarks" style="margin-bottom: 20px">
            <div class="text-left my-4">{{ $t('remarks') }}</div>
            <div class="remarks-content" v-html="mergedHtml" />
          </div>
        </template>
      </FormBuilder>
    </div>
    <el-dialog
      :lock-scroll="true"
      width="90%"
      top="0"
      :model-value="showDialogAlert"
      :close-on-click-modal="false"
      :show-close="false"
      @closed="showDialogAlert = false"
    >
      <div class="dialogMessage mb-3">{{ $t(dialogMessage) }}</div>
      <PreviewTracking
        v-if="isAddTrackingDialog && previewAddTrackingVisiblity"
        :barcode="barcode"
        :project="project"
        :trackingData="previewTrackingData"
        :fileListFormModel="fileListFormModel"
      />
      <template #footer>
        <span class="dialog-footer">
          <div>
            <el-button
              type="primary"
              @click="() => (isAddTrackingDialog ? submit(false) : submit(true))"
              class="full-w-button"
            >
              {{ $t('OK') }}
            </el-button>
          </div>
          <div v-if="shouldShowBulkButton">
            <el-button type="primary" @click="onRouteTo(bulkButtonRoute)" class="full-w-button icon-button">
              <IconScanQR />
              {{ bulkButtonLabel }}
            </el-button>
          </div>
          <div v-if="shouldShowRfidButton">
            <el-button type="primary" @click="onRouteTo(rfidButtonRoute)" class="full-w-button icon-button">
              <IconScanRfid />
              {{ rfidButtonLabel }}
            </el-button>
          </div>
          <div>
            <el-button @click="showDialogAlert = false" class="full-w-button">
              {{ $t('cancel') }}
            </el-button>
          </div>
        </span>
      </template>
    </el-dialog>
    <SkippingCustomValidationDialog
      :visible="showSkipValidationDialog"
      :message="skipValidationMessage"
      title=""
      @proceed="proceedWithSkippedValidation"
      @close="showSkipValidationDialog = false"
    />
    <ReviewCalculationDialog v-model:isShow="isShowCalculationDialog" :content="calculationContent" />

    <!-- Load Data Confirmation Dialog -->
    <el-dialog
      v-model="showLoadDataDialog"
      :title="$t('load_saved_data')"
      width="90%"
      center
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="dialogMessage mb-3">
        {{ $t('there_is_saved_data') }}
      </div>
      <template #footer>
        <span class="dialog-footer">
          <div>
            <el-button type="primary" @click="loadFromLocalStorage" class="full-w-button">
              {{ $t('yes_load_data') }}
            </el-button>
          </div>
          <div>
            <el-button @click="skipLoadFromLocalStorage" class="full-w-button">
              {{ $t('no_start_fresh') }}
            </el-button>
          </div>
        </span>
      </template>
    </el-dialog>
  </div>
</template>
<script lang="ts">
import BarcodeLabel from '@/components/BarcodeLabel.vue'
import ReviewCalculationDialog from '@/components/dialog/ReviewCalculationDialog.vue'
import FormBuilder, { IFormButton } from '@/components/FormBuilder.vue'
import BarcodeTypesMixin from '@/components/mixins/BarcodeTypesMixin.vue'
import CustomFieldsMixin from '@/components/mixins/CustomFieldsMixin.vue'
import PreviewTracking from '@/components/PreviewTracking.vue'
import SkippingCustomValidationDialog from '@/components/SkippingCustomValidationDialog.vue'
import IconScanRfid from '@/components/svg/IconRfid.vue'
import IconScanQR from '@/components/svg/IconScanQR.vue'
import { BARCODE_ACTION_COMPLETE, RESET_ALL_BARCODE_BLOB, SAVE_BARCODE } from '@/store/actions'
import { compileMessage, editTracking, reviewCalculation, tracking } from '@/utils/api'
import { isBizSdAvailable } from '@/utils/bizBrowser/sendToBizBrowser'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'
import errorHandler, { errorComparisonHandler, processError } from '@/utils/errorHandler'
import {
  flattenCustomFields,
  getDynamicLabel,
  hasComparisonError,
  isDataNotEmpty,
  isHideVisibilityConfig,
  isJson,
} from '@/utils/helpers'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import mitt from '@/utils/mitt'
import { cloneDeep } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import { ECustomButtonFormBuilderType, ECustomFieldType } from 'smartbarcode-web-core/src/utils/enums'
import {
  IActivationCustomField,
  IBarcode,
  IBarcodeDefinitionType,
  ICustomFieldData,
  IFieldSettings,
  ILocation,
  ITrackingRequestBody,
  TError,
  TFieldNotiSettings,
} from 'smartbarcode-web-core/src/utils/types/index'
import { mixins, Options } from 'vue-class-component'
import { ProvideReactive, Watch } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeLabel,
    FormBuilder,
    PreviewTracking,
    ReviewCalculationDialog,
    SkippingCustomValidationDialog,
    IconScanQR,
    IconScanRfid,
  },
  emits: ['submit'],
  name: 'TrackView',
})
export default class TrackView extends mixins(CustomFieldsMixin, BarcodeTypesMixin) {
  @ProvideReactive() isShowArrow = false
  imagesFromOCR: Record<string, string[]> = {}
  temporaryFieldsOCRImages: Record<string, Record<string, string[]>> = {}
  loading = false
  showDialogAlert = false
  showSkipValidationDialog = false
  showLoadDataDialog = false
  errorDialogMessage = ''
  skipValidationMessage = ''
  selectedTarget = ''
  targetChoices = [] as Array<{
    key: string
    name: string
    order?: number
  }>

  dialogMessage = ''
  location: ILocation | null = null
  isShowCalculationDialog = false
  calculationContent = ''
  isFloatingButtonVisible = true
  observer: IntersectionObserver | null = null
  targetRefButton = {} as HTMLElement | null

  isExpanded = false
  formBuilderKeyCounter = 0
  buttonsArrayInTrack: Array<IFormButton> = [
    {
      id: 'skipButton',
      nativeType: 'button',
      label: 'SKIP',
      type: 'danger',
      inputType: ECustomButtonFormBuilderType.BUTTON,
      onClick: () => {
        this.confirmSkipTrackingDialog()
      },
    },
  ]

  // Method to toggle expansion
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  get currentBarcodeTypeInfo(): IBarcodeDefinitionType {
    return this.$store.state.project.details.barcodeTypes[this.barcode.barcodeType]
  }

  get isFABButtonAvailable() {
    return this.currentBarcodeTypeInfo?.isFloatingActionButton || false
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get project() {
    return this.$store.state.project.details
  }

  get forms() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.trackPointForms
  }

  get isQuickTrackingMode() {
    return this.$store.getters.isQuickTrackingModeEnabled
  }

  get currentTrackPointName() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name
  }

  get customForm(): Record<string, IFieldSettings | TFieldNotiSettings> {
    const trackingForm = this.forms && cloneDeep(this.forms[this.selectedTarget])
    if (!trackingForm?.customFields) {
      trackingForm.customFields = {} as Record<string, IActivationCustomField>
    }
    const orderList = Object.values(trackingForm?.customFields) as IActivationCustomField[]
    let maxOrder = orderList
      .map((item) => item.order)
      .reduce((accumulator, currentValue) => {
        return Math.max(accumulator, currentValue)
      }, -Infinity)

    if (this.currentBarcodeTypeInfo?.activationFields?.customFields) {
      Object.entries(this.currentBarcodeTypeInfo?.activationFields?.customFields).forEach(([key, value]) => {
        if (value.fieldType === ECustomFieldType.ADJUSTMENT) {
          if (value?.trackPointRoutes) {
            const { trackPointRoutes, minValue, maxValue, ...rest } = value
            for (const item of trackPointRoutes) {
              if (item.from === this.currentTrackPointKey && item.to === this.selectedTarget) {
                maxOrder = maxOrder + 1
                rest.order = maxOrder
                if (item?.customLabel) {
                  rest.label = item.customLabel
                }
                trackingForm.customFields[key] = rest as IActivationCustomField
              }
            }
          }
        }
      })
    }
    return trackingForm
  }

  get isAuth(): boolean {
    return this.$store.getters.isAuth
  }

  get previewAddTrackingVisiblity(): boolean {
    const previewVisibility = this.currentBarcodeTypeInfo?.uiConfig?.previewAddTrackingVisiblity || ''
    return !isHideVisibilityConfig(previewVisibility, this.isAuth, false)
  }

  previewTrackingData = {}
  async loadPreviewTrackingData() {
    const calculationFields = await Object.entries(this.selectedFormSchema)
      .filter((entry) => (entry[1] as IFieldSettings).fieldType === ECustomFieldType.CALCULATION)
      .reduce(
        async (acc, entry) => ({
          ...acc,
          [entry[0]]: {
            fieldType: ECustomFieldType.CALCULATION,
            calculation: await this.getReviewCalculation(entry[0]),
          },
        }),
        {}
      )

    this.previewTrackingData = {
      ...this.generateFormRequest.trackingData,
      customFields: {
        ...this.generateFormRequest.trackingData.customFields,
        ...calculationFields,
      },
    }
  }

  async getReviewCalculation(calculationFieldKey: string) {
    this.appendOCRImage()
    const reviewPayload = {
      field: 'trackingData',
      customFieldKey: calculationFieldKey,
      startTrackPointKey: this.currentTrackPointKey,
      endTrackPointKey: this.selectedTarget,
      barcode: {
        ...this.barcode,
        trackingData: [this.generateFormRequest.trackingData],
      },
    }

    let content = ''
    try {
      content = (await reviewCalculation(reviewPayload)).data
    } catch (e) {
      errorHandler(e as TError)
    }
    return content
  }

  async reviewCalculation(calculationFieldKey: string) {
    this.loading = true
    this.calculationContent = await this.getReviewCalculation(calculationFieldKey)
    this.isShowCalculationDialog = true
    this.loading = false
  }

  labelOfBC(bc: IBarcode): string | undefined {
    const customValues = bc?.activationData || {}
    const customFields = this.currentBarcodeTypeInfo?.activationFields || {}
    const pathKey = this.currentBarcodeTypeInfo?.uiConfig.listViewTitleLabelField || ''
    const flattenCustomValues = flattenCustomFields(
      (customValues as unknown) as Record<string, Record<string, unknown>>
    ) as Record<string, ICustomFieldData>
    const flattenFields = flattenCustomFields(
      (customFields as unknown) as Record<string, Record<string, unknown>>
    ) as Record<string, IFieldSettings>
    return getDynamicLabel(flattenFields, flattenCustomValues, pathKey)
  }

  get selectedStep(): string {
    return this.$store.state.project.details.trackPoints[this.selectedTarget]?.name
  }

  get allowSkipTracking() {
    return this.forms[this.selectedTarget].allowSkipTracking
  }

  get formBuilderKey(): string {
    // Create a reactive key that changes when formModel or selectedTarget changes
    return `${this.selectedTarget}_${this.formBuilderKeyCounter}`
  }

  get hasRemarks(): boolean {
    return !isEmpty(this.customHtml)
  }

  get customHtml(): string {
    return (
      this.barcodeType?.trackPointRouteRemarks?.find(
        (remarks) => remarks.from === this.currentTrackPointKey && remarks.to === this.selectedTarget
      )?.remarks ?? ''
    )
  }

  mergedHtml = ''
  @Watch('customHtml')
  onUpdateCustomHtml() {
    if (!this.hasRemarks) return

    const cleanedHTML = this.customHtml.replace(/(\r\n|\n|\r)/gm, '')
    if (!isEmpty(cleanedHTML)) {
      compileMessage(this.$route.params.barcodeId, this.customHtml)
        .then((res) => (this.mergedHtml = res))
        .catch((err) => errorHandler(err))
    }
  }

  get barcodeType(): IBarcodeDefinitionType {
    return this.project?.barcodeTypes?.[this.barcode?.barcodeType]
  }

  get barcodeName(): string {
    return this.barcodeType.name || ''
  }

  get isAddTrackingDialog() {
    return this.dialogMode === 'add-tracking'
  }

  dialogMode = 'add-tracking'
  async confirmSubmitDialog() {
    if (!this.showDialogAlert) {
      this.dialogMode = 'add-tracking'
      this.dialogMessage = 'confirm_add_track_data'
      await this.loadPreviewTrackingData()
      this.showDialogAlert = true
    }
  }

  confirmSkipTrackingDialog() {
    if (!this.showDialogAlert) {
      this.dialogMode = 'skip-add-tracking'
      this.dialogMessage = 'confirm_skip_track_data'
      this.showDialogAlert = true
    }
  }

  get isAllowBulkUpdateAtCurrentTrackPoint() {
    return this.currentBarcodeTypeInfo?.bulkUpdatableTrackPoints.includes(this.currentTrackPointKey)
  }

  onRouteTo(view: string, id?: string) {
    this.showDialogAlert = false

    const nextSelectedTrackingPoint: string =
      (this.targetChoices.find((val) => val.key === this.selectedTarget)?.name as string) || ''

    localStorage.tempRequestForMultipleBarcodeScan = JSON.stringify(this.generateFormRequest)

    this.$router.replace({
      name: view,
      params: {
        project: this.$store.getters.projectParam,
        barcodeId: id || this.barcode?.id,
        nextTrackName: nextSelectedTrackingPoint || '',
      },
    })
  }

  async submit(isSkip: boolean) {
    try {
      this.showDialogAlert = false
      this.loading = true
      const request = {
        ...this.generateFormRequest,
        selectedNextTrackpoint: this.nextSelectTrackingPointName,
        isSkip,
        isDryRun: false,
      }
      if (this.$route.params.keyEdit) {
        await editTracking(request)
      } else {
        await tracking(request)
      }
      this.imagesFromOCR = {}
      this.temporaryFieldsOCRImages = {}

      this.$store.commit(RESET_ALL_BARCODE_BLOB)
      this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'tracked')
      this.cleanTrackingDataDraft()
      this.$router.push({
        name: 'detail',
        params: { barcodeId: this.barcode.id },
      })
    } catch (error) {
      const modelObject = {
        barcodeType: this.barcodeName,
        fTP1: this.currentTrackPointName,
        tTP2: this.targetChoices.find((val) => val.key === this.selectedTarget)?.name || '',
      }
      const isSkipCustomValidator = this.isSkipCustomValidator(error as TError)
      if (isSkipCustomValidator) {
        this.handleSkippedCustomValidation(error as TError, modelObject)
        return
      }

      if (hasComparisonError(error as TError)) {
        errorComparisonHandler(error as TError, this.currentBarcodeTypeInfo?.activationFields.customFields)
      } else {
        errorHandler(error as TError, modelObject)
      }
    } finally {
      this.loading = false
    }
  }

  cleanTrackingDataDraft() {
    const keyPrefix = `${this.project.code}.${this.barcode.id}.trackingData.${this.currentTrackPointKey}`
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(keyPrefix)) {
        localStorage.removeItem(key)
      }
    })
  }

  isSkipCustomValidator(error: TError) {
    if (typeof error === 'string') {
      if (isJson(error)) {
        const jsonObject = JSON.parse(error)
        return jsonObject.IsSkipCustomValidator ?? false
      }
    }
    return false
  }

  handleSkippedCustomValidation(error: TError, fieldTemplate?: Record<string, string>) {
    const { errorDetails } = processError(error, fieldTemplate)
    this.skipValidationMessage = errorDetails
    this.showSkipValidationDialog = true
  }

  async proceedWithSkippedValidation() {
    try {
      this.showSkipValidationDialog = false
      this.loading = true
      const request = {
        ...this.generateFormRequest,
        selectedNextTrackpoint: this.nextSelectTrackingPointName,
        isSkip: false,
        isDryRun: false,
        IsSkipCustomValidator: true,
      }

      if (this.$route.params.keyEdit) {
        await editTracking(request)
      } else {
        await tracking(request)
      }

      this.imagesFromOCR = {}
      this.temporaryFieldsOCRImages = {}

      this.$store.commit(RESET_ALL_BARCODE_BLOB)
      this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'tracked')
      this.cleanTrackingDataDraft()
      this.$router.push({
        name: 'detail',
        params: { barcodeId: this.barcode.id },
      })
    } catch (error) {
      const modelObject = {
        barcodeType: this.barcodeName,
        fTP1: this.currentTrackPointName,
        tTP2: this.targetChoices.find((val) => val.key === this.selectedTarget)?.name || '',
      }
      errorHandler(error as TError, modelObject)
    } finally {
      this.loading = false
    }
  }

  checkAndPromptForLocalStorageData() {
    const valueFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraft)
    if (!valueFromLocalStorage) return
    const parsedValue = JSON.parse(valueFromLocalStorage)
    if (valueFromLocalStorage && isDataNotEmpty(parsedValue)) {
      this.isPreventSaveLocal = true
      this.showLoadDataDialog = true
    }
  }

  loadFromLocalStorage() {
    this.showLoadDataDialog = false
    const valueFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraft)
    if (!valueFromLocalStorage) return
    const parsedValue = JSON.parse(valueFromLocalStorage)
    if (parsedValue && typeof parsedValue === 'object') {
      this.formModel = parsedValue
      this.generateCustomFormModel(undefined, true)
    } else {
      localStorage.removeItem(this.keyTrackingDataDraft)
    }
    this.isPreventSaveLocal = false
    this.formBuilderKeyCounter++
  }

  skipLoadFromLocalStorage() {
    this.showLoadDataDialog = false
    this.formModel = {}
    this.generateCustomFormModel(undefined)
    this.isPreventSaveLocal = false
    this.formBuilderKeyCounter++
  }

  // Editing a saved tracking event reloads values that were already composed, so
  // composing again would append the same parts twice: abc-001 -> abc-001-001.
  get isComposerEnabled(): boolean {
    return !this.$route.params.keyEdit
  }

  get generateFormRequest() {
    this.appendOCRImage()
    const generatedCustomDataForRequest = this.generateCustomDataForRequest
    const { location, ...other } = generatedCustomDataForRequest
    const capturedDateTime = localStorage.getItem(LOCAL_STORAGE_ITEM.CAPTURE_TIMESTAMP)
    const requestBody: ITrackingRequestBody = {
      barcodeIds: [this.barcode.id],
      trackingData: {
        currentTrackPointKey: this.barcode.currentTrackPointKey,
        nextTrackPointKey: this.selectedTarget,
        location,
        customFields: {
          ...other,
        },
        ...(capturedDateTime && { audit: { capturedDateTime } }),
      },
    }

    return requestBody
  }

  isPreventSaveLocal = false

  @Watch('formModel', { deep: true })
  onUpdateFormModel() {
    if (this.isPreventSaveLocal) return
    localStorage.setItem(this.keyTrackingDataDraft, JSON.stringify(this.formModel))
  }

  @Watch('selectedTarget')
  updateCustomFormModel() {
    if (!this.$route.params.keyEdit) {
      this.checkAndPromptForLocalStorageData()
      this.formModel = {}
      this.generateCustomFormModel(undefined)
    }
  }

  get keyTrackingDataDraft() {
    return (
      this.project.code +
      '.' +
      this.barcode.id +
      '.trackingData.' +
      this.currentTrackPointKey +
      '.' +
      this.selectedTarget
    )
  }

  appendOCRImage() {
    Object.keys(this.imagesFromOCR ?? {}).forEach((key) => {
      const current = (this.formModel?.[key] ?? []) as string[]
      const incoming = (this.imagesFromOCR?.[key] ?? []).filter((image: string) => !current.includes(image))
      if (incoming.length) this.formModel[key] = [...current, ...incoming]
    })
  }

  isDisabledEditing(key: string): boolean {
    // this function is used to make sure only the selected node is edited, prevent chosing other nodes
    let result = false
    if (this.$route.params.keyEdit) {
      if (key === this.$route.params.keyEdit) {
        result = false
      } else {
        result = true
      }
    }
    return result
  }

  mounted() {
    const project = this.$store.state.project.details
    if (this.forms) {
      const keyEdit = this.$route.params.keyEdit
      if (keyEdit) {
        if (this.barcode) {
          const clonedBarcode = { ...this.barcode }
          clonedBarcode.currentTrackPointKey = this.$route.params.prevTrackPointKey
          this.$store.dispatch(SAVE_BARCODE, { ...clonedBarcode })
        }
      }
      this.targetChoices = Object.keys(this.forms)
        .map((stepKey) => ({
          key: stepKey,
          name: project.trackPoints[stepKey].name,
          order: this.forms[stepKey].order,
        }))
        .filter(
          (choice) =>
            project.code !== 'mitsui01' ||
            this.currentTrackPointName === 'C新横浜店配' ||
            choice.name !== 'C新横浜空番重出庫'
        )

      if (this.targetChoices.length > 0) {
        this.targetChoices.sort((a, b) => (a.order && b.order && a.order - b.order) || 0)
        this.selectedTarget = keyEdit || this.targetChoices[0].key
      }
      if (keyEdit) {
        if (this.barcode) {
          this.formModel = this.convertProcessedDataToOriginal(
            this.barcode.trackingData[this.$route.params.index].customFields
          )
        } else {
          this.generateCustomFormModel(undefined, true)
        }
      } else {
        this.generateCustomFormModel(undefined, true)
      }
    }

    mitt.on('update:trackInfo', (event) => {
      this.formModel = Object.assign(this.formModel, event)
    })

    mitt.on('update:fileList', (event) => {
      this.fileListFormModel = Object.assign(this.fileListFormModel, event)
    })

    mitt.on('update:imageOCR', (event) => this.saveOCRImage(event))
  }

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  get isBizBrowserEnabled() {
    return isBizSdAvailable()
  }

  get shouldShowBulkButton() {
    return this.isAllowBulkUpdateAtCurrentTrackPoint || !this.isAddTrackingDialog
  }

  get shouldShowRfidButton() {
    return this.isBizBrowserEnabled && this.shouldShowBulkButton
  }

  get bulkButtonRoute() {
    if (this.isKeyenceEnterpriseBrowser) {
      return this.isAddTrackingDialog
        ? 'bulk-tracking-with-keyence-handyterminal'
        : 'bulk-skip-tracking-with-keyence-handyterminal'
    }
    return this.isAddTrackingDialog ? 'bulk-tracking' : 'bulk-skip-tracking'
  }

  get rfidButtonRoute() {
    return this.isAddTrackingDialog ? 'bulk-tracking-rfid' : 'bulk-skip-tracking-rfid'
  }

  get bulkButtonLabel() {
    return this.$t(
      this.isAddTrackingDialog
        ? 'ok_scan_barcode_to_track_multiple_barcodes'
        : 'ok_scan_barcode_to_skip_multiple_barcodes_tracking'
    )
  }

  get rfidButtonLabel() {
    return this.$t(
      this.isAddTrackingDialog
        ? 'ok_scan_rfid_to_track_multiple_barcodes'
        : 'ok_scan_rfid_to_skip_multiple_barcodes_tracking'
    )
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

.current-tracking-point-container {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid $border-color;
  padding: 8px 16px;
  margin-bottom: 20px;
  margin-top: 20px;
  background: #dadada;
  border-radius: 8px;
  line-height: 18px;
  text-align: left;
}

.remarks-content {
  @include label-box;
  text-align: left;
  border-radius: 5px !important;
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.section {
  margin-top: 12px;
  margin-bottom: 20px;
}

hr.line-break {
  margin: 5px 0 15px 0;
}

.section-header {
  line-height: 2;
  text-align: left;

  &.shrink {
    margin-bottom: 0;
  }
}

.info {
  color: $main;
}

.next-trackingPoint {
  padding: 4px 0;
}

.section-trackpoints {
  .el-radio {
    margin-right: 0px;
  }
}

.icon-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 8px !important;

  :deep(span) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  svg {
    width: 32px;
    height: 32px;
  }
}
</style>
